import { createContext, useContext, useEffect, useReducer, type ReactNode } from 'react';
import { User, AuthContextType } from '@/types';
import { authApi } from '@/utils/api';

// Estado inicial del contexto
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
};

// Tipos de acciones
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User };

// Reducer para manejar el estado
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    
    default:
      return state;
  }
}

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar el contexto
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}

// Props del provider
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider del contexto de autenticación
 * Maneja el estado global de autenticación y proporciona métodos para login/logout
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          // No hay token, usuario no autenticado (estado normal)
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        // Verificar token con el backend
        const response = await authApi.getProfile();
        if (response.success && response.data) {
          const { user } = response.data;
          
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user },
          });
        } else {
          // Token inválido, limpiar localStorage silenciosamente
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error: any) {
        // Solo loguear errores inesperados, no errores de autenticación (401)
        const statusCode = error?.response?.status || error?.status;
        if (statusCode !== 401) {
          console.error('Error verificando autenticación:', error);
        }
        
        // Limpiar localStorage si es un error de autenticación (401 = token inválido/expirado)
        if (statusCode === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        }
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  /**
   * Función para iniciar sesión.
   * Reintenta hasta 3 veces con backoff (1s, 2s, 3s) solo ante errores de red
   * (backend no disponible, ECONNRESET/ECONNREFUSED al reiniciar).
   */
  const login = async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    const maxRetries = 3;
    const backoffMs = [1000, 2000, 3000];
    let lastError: unknown;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await authApi.login(email, password);

        if (response.success && response.data) {
          const { user, token } = response.data;
          localStorage.setItem('auth_token', token);
          localStorage.setItem('user', JSON.stringify(user));
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user } });
          return;
        }

        throw new Error(response.message || 'Error al iniciar sesión');
      } catch (error) {
        lastError = error;
        const isNetworkError = !!(error as { isNetworkError?: boolean })?.isNetworkError;

        if (isNetworkError && attempt < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, backoffMs[attempt]));
          continue;
        }

        dispatch({ type: 'SET_LOADING', payload: false });
        throw error;
      }
    }

    dispatch({ type: 'SET_LOADING', payload: false });
    throw lastError;
  };

  /**
   * Función para cerrar sesión
   */
  const logout = async () => {
    try {
      // Llamar al endpoint de logout
      await authApi.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      // Limpiar localStorage y estado
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
    }
  };

  /**
   * Función para actualizar el perfil del usuario
   */
  const updateUser = async (data: { firstName: string; lastName: string; email: string; phone?: string }) => {
    try {
      const response = await authApi.updateProfile(data);
      
      if (response.success && response.data) {
        const { user } = response.data;
        
        // Actualizar localStorage
        localStorage.setItem('user', JSON.stringify(user));
        
        // Actualizar estado
        dispatch({
          type: 'UPDATE_USER',
          payload: user,
        });
      } else {
        throw new Error(response.message || 'Error al actualizar el perfil');
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Función para cambiar la contraseña del usuario
   */
  const changePassword = async (data: { currentPassword: string; newPassword: string }) => {
    try {
      const response = await authApi.changePassword(data);
      
      if (!response.success) {
        throw new Error(response.message || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      throw error;
    }
  };

  // Valor del contexto
  const value: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login,
    logout,
    updateUser,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
